import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, FormHelperText, TextField, Alert, Divider, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { setMachineDetailsData } from '../../redux/machineDetailsDataSlice';
import { setSuccessfullModifyDialog } from '../../redux/successfullModifyDialogSlice';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import Iconify from '../Iconify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import {
  ARTICLE_PUBLICATION_STATUSES,
  PUBLICATION_STATUS_LABELS,
} from '../../utils/articleItemMediaUtils';
import { cleanRichText } from '../../utils/richText';
import RichTextEditor from '../richText/RichTextEditor';
import useContentFormDialog from '../richText/useContentFormDialog';
import ContentMediaGallery from '../contentMedia/ContentMediaGallery';
import { getContentGalleryItems } from '../../utils/contentMediaUtils';
import { toContentItemShape } from './machineContentApi';
import '../../css/users/addUserDialog.css';
import '../../css/invoices/addInvoiceDialog.css';

export const EditArticleItemDialog = ({ editArticleItem, setEditArticleItem }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [errMsg, setErrMsg] = useState(null);
    const [loading, setLoading] = useState(false);
    const [itemData, setItemData] = useState(toContentItemShape(editArticleItem.data));
    const machineDetailsData = useSelector((state) => state.MachineDetailsData);

    const applyItem = useCallback((item) => {
        const shaped = toContentItemShape(item);
        setItemData(shaped);
        dispatch(setMachineDetailsData({
            ...machineDetailsData,
            machine_content: (machineDetailsData.machine_content || []).map((entry) => (entry.id === item.id ? item : entry)),
        }));
    }, [dispatch, machineDetailsData]);

    const reloadItem = useCallback(async () => {
        const { data, status } = await axios.get(`machine-content/${editArticleItem.data.id}`);
        if (status === 200 || status === 201) {
            applyItem(data);
        }
    }, [applyItem, editArticleItem.data.id]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            title: editArticleItem.data?.title || '',
            description: editArticleItem.data?.description || '',
            publication_status: editArticleItem.data?.publication_status || ARTICLE_PUBLICATION_STATUSES.published,
        },
        validationSchema: Yup.object().shape({
            title: Yup.string().required(t('isRequired')),
            description: Yup.string().optional(),
            publication_status: Yup.string().required(t('isRequired')),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const formData = new FormData();
                formData.append('title', values.title || '');
                formData.append('description', cleanRichText(values.description));
                formData.append('publication_status', values.publication_status);
                const response = await axios.patch(`machine-content/${editArticleItem.data.id}`, formData);
                if (response.status === 200 || response.status === 201) {
                    dispatch(setSuccessfullModifyDialog({ open: true, type: t('Modification du contenu') }));
                    applyItem(response.data);
                    setEditArticleItem({ data: null, open: false });
                    setErrMsg(null);
                }
                setLoading(false);
            } catch (error) {
                setLoading(false);
                setErrMsg(error.response?.data?.message || error.message || 'erreur lors de la modification du contenu');
                CustomErrorLogger(error, 'error editing machine content');
            }
        },
    });

    const { handleSubmit, values, handleChange, handleBlur, touched, errors, setFieldValue } = formik;
    const dialogProps = useContentFormDialog();
    const handleClose = useCallback(() => {
        setEditArticleItem({ data: null, open: false });
        setErrMsg(null);
    }, [setEditArticleItem]);

    return (
        <Dialog open={editArticleItem.open} {...dialogProps}>
            <IconButton onClick={handleClose} className='addUser_icon_button'>
                <Iconify icon="material-symbols:close" className='addUser_close_icon' />
            </IconButton>
            <DialogTitle className='addUser_dialog_title'>Modifier le contenu pédagogique</DialogTitle>
            <Divider />
            {errMsg && <Alert severity="error">{errMsg}</Alert>}
            <form className='contentFormDialog_form' onSubmit={handleSubmit}>
                <DialogContent className='addUser_dialog_content contentFormDialog_content'>
                    <Box className="addUser_helper_selct_box">
                        <FormControl fullWidth>
                            <InputLabel id="edit-publication-status-label">Statut</InputLabel>
                            <Select labelId="edit-publication-status-label" label="Statut" name="publication_status" value={values.publication_status} onChange={handleChange}>
                                {Object.values(ARTICLE_PUBLICATION_STATUSES).map((status) => (
                                    <MenuItem key={status} value={status}>{PUBLICATION_STATUS_LABELS[status]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box className="addUser_helper_selct_box">
                        <FormHelperText className="addUserInputTitle">{t('Titre')}</FormHelperText>
                        <TextField fullWidth value={values.title} className='addUser_InputField' onChange={handleChange} onBlur={handleBlur} name="title" error={touched.title && Boolean(errors.title)} helperText={touched.title && errors.title} />
                    </Box>
                    <Box className="addUser_helper_selct_box contentFormDialog_description">
                        <FormHelperText className="addMachineNote_InputField">{t('Description')} (optionnel)</FormHelperText>
                        <RichTextEditor value={values.description} onChange={(html) => setFieldValue('description', html)} placeholder={t('Description')} />
                    </Box>
                    <DialogActions className='addUser_dialog_actions'>
                        <Button type="button" className='addUser_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                        <LoadingButton className='addUser_button_valider' loading={loading} variant="contained" type="submit">{t('Valider')}</LoadingButton>
                    </DialogActions>
                    <ContentMediaGallery
                        scope="machine"
                        parentId={editArticleItem.data?.id}
                        items={getContentGalleryItems(itemData)}
                        onPersistedChange={reloadItem}
                        maxDocumentVideoBytes={500 * 1024 * 1024}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button type="button" className='addUser_button_annuler' onClick={handleClose}>{t('Fermer')}</Button>
                    </Box>
                </DialogContent>
            </form>
        </Dialog>
    );
};
